import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { JpClassApi } from "../../api/JpClassApi";
import type { ClassLocationFetchOptions } from "../../generated/dto/ClassLocationFetchOptions";
import type { ClassLocationStaff } from "../../generated/dto/ClassLocationStaff";
import type { CreateJpClass } from "../../generated/dto/CreateJpClass";
import type { CreateJpClassAttendance } from "../../generated/dto/CreateJpClassAttendance";
import type { CreateJpClassSession } from "../../generated/dto/CreateJpClassSession";
import type { CreateJpClassSignup } from "../../generated/dto/CreateJpClassSignup";
import type { CreateJpClassStagger } from "../../generated/dto/CreateJpClassStagger";
import type { CreateJpClassType } from "../../generated/dto/CreateJpClassType";
import type { CreateJpClassTypeOverride } from "../../generated/dto/CreateJpClassTypeOverride";
import type { CreateJpClassWlResult } from "../../generated/dto/CreateJpClassWlResult";
import type { DeleteJpClassAttendanceStaff } from "../../generated/dto/DeleteJpClassAttendanceStaff";
import type { DeleteJpClassSessionStaff } from "../../generated/dto/DeleteJpClassSessionStaff";
import type { DeleteJpClassSignupStaff } from "../../generated/dto/DeleteJpClassSignupStaff";
import type { DeleteJpClassStaff } from "../../generated/dto/DeleteJpClassStaff";
import type { DeleteJpClassStaggerStaff } from "../../generated/dto/DeleteJpClassStaggerStaff";
import type { DeleteJpClassTypeOverrideStaff } from "../../generated/dto/DeleteJpClassTypeOverrideStaff";
import type { DeleteJpClassTypeStaff } from "../../generated/dto/DeleteJpClassTypeStaff";
import type { DeleteJpClassWlResultStaff } from "../../generated/dto/DeleteJpClassWlResultStaff";
import type { JpClassAttendanceFetchOptions } from "../../generated/dto/JpClassAttendanceFetchOptions";
import type { JpClassAttendanceStaff } from "../../generated/dto/JpClassAttendanceStaff";
import type { JpClassFetchOptions } from "../../generated/dto/JpClassFetchOptions";
import type { JpClassMember } from "../../generated/dto/JpClassMember";
import type { JpClassRef } from "../../generated/dto/JpClassRef";
import type { JpClassSearchParams } from "../../generated/dto/JpClassSearchParams";
import type { JpClassSessionFetchOptions } from "../../generated/dto/JpClassSessionFetchOptions";
import type { JpClassSessionStaff } from "../../generated/dto/JpClassSessionStaff";
import type { JpClassSignupFetchOptions } from "../../generated/dto/JpClassSignupFetchOptions";
import type { JpClassSignupStaff } from "../../generated/dto/JpClassSignupStaff";
import type { JpClassStaff } from "../../generated/dto/JpClassStaff";
import type { JpClassStaffSearchResult } from "../../generated/dto/JpClassStaffSearchResult";
import type { JpClassStaggerFetchOptions } from "../../generated/dto/JpClassStaggerFetchOptions";
import type { JpClassStaggerStaff } from "../../generated/dto/JpClassStaggerStaff";
import type { JpClassTypeFetchOptions } from "../../generated/dto/JpClassTypeFetchOptions";
import type { JpClassTypeOverrideFetchOptions } from "../../generated/dto/JpClassTypeOverrideFetchOptions";
import type { JpClassTypeOverrideStaff } from "../../generated/dto/JpClassTypeOverrideStaff";
import type { JpClassTypeSearchParams } from "../../generated/dto/JpClassTypeSearchParams";
import type { JpClassTypeStaff } from "../../generated/dto/JpClassTypeStaff";
import type { JpClassTypeStaffSearchResult } from "../../generated/dto/JpClassTypeStaffSearchResult";
import type { JpClassWlResultFetchOptions } from "../../generated/dto/JpClassWlResultFetchOptions";
import type { JpClassWlResultStaff } from "../../generated/dto/JpClassWlResultStaff";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { RatingFetchOptions } from "../../generated/dto/RatingFetchOptions";
import type { RatingStaff } from "../../generated/dto/RatingStaff";
import type { UpdateJpClass } from "../../generated/dto/UpdateJpClass";
import type { UpdateJpClassAttendance } from "../../generated/dto/UpdateJpClassAttendance";
import type { UpdateJpClassSession } from "../../generated/dto/UpdateJpClassSession";
import type { UpdateJpClassSignup } from "../../generated/dto/UpdateJpClassSignup";
import type { UpdateJpClassStagger } from "../../generated/dto/UpdateJpClassStagger";
import type { UpdateJpClassType } from "../../generated/dto/UpdateJpClassType";
import type { UpdateJpClassTypeOverride } from "../../generated/dto/UpdateJpClassTypeOverride";
import type { UpdateJpClassWlResult } from "../../generated/dto/UpdateJpClassWlResult";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class JpClassApiClient implements JpClassApi {
  constructor(private readonly client = useClient()) {}

  async getJpClassTypeStaff(id: number, options?: JpClassTypeFetchOptions): Promise<JpClassTypeStaff> {
    const { data } = await this.client.get<unknown>(`/jpclasstype/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeFetchOptions") });
    return deserializeCbiDto<JpClassTypeStaff>(data, "generated.dto.JpClassTypeStaff");
  }

  async getJpClassTypeOverrideStaff(id: number, options?: JpClassTypeOverrideFetchOptions): Promise<JpClassTypeOverrideStaff> {
    const { data } = await this.client.get<unknown>(`/jpclasstypeoverride/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeOverrideFetchOptions") });
    return deserializeCbiDto<JpClassTypeOverrideStaff>(data, "generated.dto.JpClassTypeOverrideStaff");
  }

  async getJpClassStaff(id: number, options?: JpClassFetchOptions): Promise<JpClassStaff> {
    const { data } = await this.client.get<unknown>(`/jpclass/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.JpClassFetchOptions") });
    return deserializeCbiDto<JpClassStaff>(data, "generated.dto.JpClassStaff");
  }

  async getJpClassSessionStaff(id: number, options?: JpClassSessionFetchOptions): Promise<JpClassSessionStaff> {
    const { data } = await this.client.get<unknown>(`/jpclasssession/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.JpClassSessionFetchOptions") });
    return deserializeCbiDto<JpClassSessionStaff>(data, "generated.dto.JpClassSessionStaff");
  }

  async getJpClassStaggerStaff(id: number, options?: JpClassStaggerFetchOptions): Promise<JpClassStaggerStaff> {
    const { data } = await this.client.get<unknown>(`/jpclassstagger/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.JpClassStaggerFetchOptions") });
    return deserializeCbiDto<JpClassStaggerStaff>(data, "generated.dto.JpClassStaggerStaff");
  }

  async getJpClassSignupStaff(id: number, options?: JpClassSignupFetchOptions): Promise<JpClassSignupStaff> {
    const { data } = await this.client.get<unknown>(`/jpclasssignup/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.JpClassSignupFetchOptions") });
    return deserializeCbiDto<JpClassSignupStaff>(data, "generated.dto.JpClassSignupStaff");
  }

  async getJpClassAttendanceStaff(id: number, options?: JpClassAttendanceFetchOptions): Promise<JpClassAttendanceStaff> {
    const { data } = await this.client.get<unknown>(`/jpclassattendance/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.JpClassAttendanceFetchOptions") });
    return deserializeCbiDto<JpClassAttendanceStaff>(data, "generated.dto.JpClassAttendanceStaff");
  }

  async getJpClassWlResultStaff(id: number, options?: JpClassWlResultFetchOptions): Promise<JpClassWlResultStaff> {
    const { data } = await this.client.get<unknown>(`/jpclasswlresult/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.JpClassWlResultFetchOptions") });
    return deserializeCbiDto<JpClassWlResultStaff>(data, "generated.dto.JpClassWlResultStaff");
  }

  async createJpClassTypeStaff(source: CreateJpClassType): Promise<JpClassTypeStaff> {
    const { data } = await this.client.post<unknown>(`/jpclasstype/staff`, serializeCbiDto(source, "generated.dto.CreateJpClassType"));
    return deserializeCbiDto<JpClassTypeStaff>(data, "generated.dto.JpClassTypeStaff");
  }

  async createJpClassTypeOverrideStaff(source: CreateJpClassTypeOverride): Promise<JpClassTypeOverrideStaff> {
    const { data } = await this.client.post<unknown>(`/jpclasstypeoverride/staff`, serializeCbiDto(source, "generated.dto.CreateJpClassTypeOverride"));
    return deserializeCbiDto<JpClassTypeOverrideStaff>(data, "generated.dto.JpClassTypeOverrideStaff");
  }

  async createJpClassStaff(source: CreateJpClass): Promise<JpClassStaff> {
    const { data } = await this.client.post<unknown>(`/jpclass/staff`, serializeCbiDto(source, "generated.dto.CreateJpClass"));
    return deserializeCbiDto<JpClassStaff>(data, "generated.dto.JpClassStaff");
  }

  async createJpClassSessionStaff(source: CreateJpClassSession): Promise<JpClassSessionStaff> {
    const { data } = await this.client.post<unknown>(`/jpclasssession/staff`, serializeCbiDto(source, "generated.dto.CreateJpClassSession"));
    return deserializeCbiDto<JpClassSessionStaff>(data, "generated.dto.JpClassSessionStaff");
  }

  async createJpClassStaggerStaff(source: CreateJpClassStagger): Promise<JpClassStaggerStaff> {
    const { data } = await this.client.post<unknown>(`/jpclassstagger/staff`, serializeCbiDto(source, "generated.dto.CreateJpClassStagger"));
    return deserializeCbiDto<JpClassStaggerStaff>(data, "generated.dto.JpClassStaggerStaff");
  }

  async createJpClassSignupStaff(source: CreateJpClassSignup): Promise<JpClassSignupStaff> {
    const { data } = await this.client.post<unknown>(`/jpclasssignup/staff`, serializeCbiDto(source, "generated.dto.CreateJpClassSignup"));
    return deserializeCbiDto<JpClassSignupStaff>(data, "generated.dto.JpClassSignupStaff");
  }

  async createJpClassAttendanceStaff(source: CreateJpClassAttendance): Promise<JpClassAttendanceStaff> {
    const { data } = await this.client.post<unknown>(`/jpclassattendance/staff`, serializeCbiDto(source, "generated.dto.CreateJpClassAttendance"));
    return deserializeCbiDto<JpClassAttendanceStaff>(data, "generated.dto.JpClassAttendanceStaff");
  }

  async createJpClassWlResultStaff(source: CreateJpClassWlResult): Promise<JpClassWlResultStaff> {
    const { data } = await this.client.post<unknown>(`/jpclasswlresult/staff`, serializeCbiDto(source, "generated.dto.CreateJpClassWlResult"));
    return deserializeCbiDto<JpClassWlResultStaff>(data, "generated.dto.JpClassWlResultStaff");
  }

  async updateJpClassTypeStaff(source: UpdateJpClassType): Promise<JpClassTypeStaff> {
    const { data } = await this.client.put<unknown>(`/jpclasstype/staff/${source.typeId}`, serializeCbiDto(source, "generated.dto.UpdateJpClassType"));
    return deserializeCbiDto<JpClassTypeStaff>(data, "generated.dto.JpClassTypeStaff");
  }

  async updateJpClassTypeOverrideStaff(source: UpdateJpClassTypeOverride): Promise<JpClassTypeOverrideStaff> {
    const { data } = await this.client.put<unknown>(`/jpclasstypeoverride/staff/${source.rowId}`, serializeCbiDto(source, "generated.dto.UpdateJpClassTypeOverride"));
    return deserializeCbiDto<JpClassTypeOverrideStaff>(data, "generated.dto.JpClassTypeOverrideStaff");
  }

  async updateJpClassStaff(source: UpdateJpClass): Promise<JpClassStaff> {
    const { data } = await this.client.put<unknown>(`/jpclass/staff/${source.instanceId}`, serializeCbiDto(source, "generated.dto.UpdateJpClass"));
    return deserializeCbiDto<JpClassStaff>(data, "generated.dto.JpClassStaff");
  }

  async updateJpClassSessionStaff(source: UpdateJpClassSession): Promise<JpClassSessionStaff> {
    const { data } = await this.client.put<unknown>(`/jpclasssession/staff/${source.sessionId}`, serializeCbiDto(source, "generated.dto.UpdateJpClassSession"));
    return deserializeCbiDto<JpClassSessionStaff>(data, "generated.dto.JpClassSessionStaff");
  }

  async updateJpClassStaggerStaff(source: UpdateJpClassStagger): Promise<JpClassStaggerStaff> {
    const { data } = await this.client.put<unknown>(`/jpclassstagger/staff/${source.staggerId}`, serializeCbiDto(source, "generated.dto.UpdateJpClassStagger"));
    return deserializeCbiDto<JpClassStaggerStaff>(data, "generated.dto.JpClassStaggerStaff");
  }

  async updateJpClassSignupStaff(source: UpdateJpClassSignup): Promise<JpClassSignupStaff> {
    const { data } = await this.client.put<unknown>(`/jpclasssignup/staff/${source.signupId}`, serializeCbiDto(source, "generated.dto.UpdateJpClassSignup"));
    return deserializeCbiDto<JpClassSignupStaff>(data, "generated.dto.JpClassSignupStaff");
  }

  async updateJpClassAttendanceStaff(source: UpdateJpClassAttendance): Promise<JpClassAttendanceStaff> {
    const { data } = await this.client.put<unknown>(`/jpclassattendance/staff/${source.attendId}`, serializeCbiDto(source, "generated.dto.UpdateJpClassAttendance"));
    return deserializeCbiDto<JpClassAttendanceStaff>(data, "generated.dto.JpClassAttendanceStaff");
  }

  async updateJpClassWlResultStaff(source: UpdateJpClassWlResult): Promise<JpClassWlResultStaff> {
    const { data } = await this.client.put<unknown>(`/jpclasswlresult/staff/${source.signupId}`, serializeCbiDto(source, "generated.dto.UpdateJpClassWlResult"));
    return deserializeCbiDto<JpClassWlResultStaff>(data, "generated.dto.JpClassWlResultStaff");
  }

  async deleteJpClassTypeStaff(source: DeleteJpClassTypeStaff): Promise<void> {
    await this.client.delete(`/jpclasstype/staff/${source.typeId}`);
  }

  async deleteJpClassTypeOverrideStaff(source: DeleteJpClassTypeOverrideStaff): Promise<void> {
    await this.client.delete(`/jpclasstypeoverride/staff/${source.rowId}`);
  }

  async deleteJpClassStaff(source: DeleteJpClassStaff): Promise<void> {
    await this.client.delete(`/jpclass/staff/${source.instanceId}`);
  }

  async deleteJpClassSessionStaff(source: DeleteJpClassSessionStaff): Promise<void> {
    await this.client.delete(`/jpclasssession/staff/${source.sessionId}`);
  }

  async deleteJpClassStaggerStaff(source: DeleteJpClassStaggerStaff): Promise<void> {
    await this.client.delete(`/jpclassstagger/staff/${source.staggerId}`);
  }

  async deleteJpClassSignupStaff(source: DeleteJpClassSignupStaff): Promise<void> {
    await this.client.delete(`/jpclasssignup/staff/${source.signupId}`);
  }

  async deleteJpClassAttendanceStaff(source: DeleteJpClassAttendanceStaff): Promise<void> {
    await this.client.delete(`/jpclassattendance/staff/${source.attendId}`);
  }

  async deleteJpClassWlResultStaff(source: DeleteJpClassWlResultStaff): Promise<void> {
    await this.client.delete(`/jpclasswlresult/staff/${source.signupId}`);
  }

  async searchJpClassTypeStaff(params: JpClassTypeSearchParams, page: number, perPage: number): Promise<PaginationResult<JpClassTypeStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/jpclasstype/staff/search`, serializeCbiDto(params, "generated.dto.JpClassTypeSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<JpClassTypeStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<JpClassTypeStaffSearchResult>>(result.items, "generated.dto.JpClassTypeStaffSearchResult") };
    return result;
  }

  async searchJpClassStaff(params: JpClassSearchParams, page: number, perPage: number): Promise<PaginationResult<JpClassStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/jpclass/staff/search`, serializeCbiDto(params, "generated.dto.JpClassSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<JpClassStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<JpClassStaffSearchResult>>(result.items, "generated.dto.JpClassStaffSearchResult") };
    return result;
  }

  async searchJpClassTypeStaffFull(params: JpClassTypeSearchParams, page: number, perPage: number, options?: JpClassTypeFetchOptions): Promise<PaginationResult<JpClassTypeStaff>> {
    const { data } = await this.client.post<unknown>(`/jpclasstype/staff/search/full`, serializeCbiDto(params, "generated.dto.JpClassTypeSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.JpClassTypeFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<JpClassTypeStaff>;
    result = { ...result, items: deserializeCbiDto<Array<JpClassTypeStaff>>(result.items, "generated.dto.JpClassTypeStaff") };
    return result;
  }

  async searchJpClassStaffFull(params: JpClassSearchParams, page: number, perPage: number, options?: JpClassFetchOptions): Promise<PaginationResult<JpClassStaff>> {
    const { data } = await this.client.post<unknown>(`/jpclass/staff/search/full`, serializeCbiDto(params, "generated.dto.JpClassSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.JpClassFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<JpClassStaff>;
    result = { ...result, items: deserializeCbiDto<Array<JpClassStaff>>(result.items, "generated.dto.JpClassStaff") };
    return result;
  }

  async getJpClassTypeStaffRatingPrereqId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    const { data } = await this.client.get<unknown>(`/jpclasstype/staff/${id}/ratingprereqid`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<RatingStaff | null>(data, "generated.dto.RatingStaff");
  }

  async getJpClassTypeStaffRatingOverkillId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    const { data } = await this.client.get<unknown>(`/jpclasstype/staff/${id}/ratingoverkillid`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<RatingStaff | null>(data, "generated.dto.RatingStaff");
  }

  async getJpClassTypeStaffClassPrereqId(id: number, options?: JpClassTypeFetchOptions): Promise<JpClassTypeStaff | null> {
    const { data } = await this.client.get<unknown>(`/jpclasstype/staff/${id}/classprereqid`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeFetchOptions") });
    return deserializeCbiDto<JpClassTypeStaff | null>(data, "generated.dto.JpClassTypeStaff");
  }

  async getJpClassTypeStaffClassOverkillId(id: number, options?: JpClassTypeFetchOptions): Promise<JpClassTypeStaff | null> {
    const { data } = await this.client.get<unknown>(`/jpclasstype/staff/${id}/classoverkillid`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeFetchOptions") });
    return deserializeCbiDto<JpClassTypeStaff | null>(data, "generated.dto.JpClassTypeStaff");
  }

  async getJpClassTypeStaffTeachRatingId(id: number, options?: RatingFetchOptions): Promise<RatingStaff | null> {
    const { data } = await this.client.get<unknown>(`/jpclasstype/staff/${id}/teachratingid`, { params: serializeCbiDto(options, "generated.dto.RatingFetchOptions") });
    return deserializeCbiDto<RatingStaff | null>(data, "generated.dto.RatingStaff");
  }

  async getJpClassTypeStaffClasses(id: number, options?: JpClassFetchOptions): Promise<Array<JpClassStaff>> {
    const { data } = await this.client.get<unknown>(`/jpclasstype/staff/${id}/classes`, { params: serializeCbiDto(options, "generated.dto.JpClassFetchOptions") });
    return deserializeCbiDto<Array<JpClassStaff>>(data, "generated.dto.JpClassStaff");
  }

  async getJpClassTypeStaffTypesRequiringThisAsPrereq(id: number, options?: JpClassTypeFetchOptions): Promise<Array<JpClassTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/jpclasstype/staff/${id}/typesrequiringthisasprereq`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeFetchOptions") });
    return deserializeCbiDto<Array<JpClassTypeStaff>>(data, "generated.dto.JpClassTypeStaff");
  }

  async getJpClassTypeStaffTypesRequiringThisAsOverkill(id: number, options?: JpClassTypeFetchOptions): Promise<Array<JpClassTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/jpclasstype/staff/${id}/typesrequiringthisasoverkill`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeFetchOptions") });
    return deserializeCbiDto<Array<JpClassTypeStaff>>(data, "generated.dto.JpClassTypeStaff");
  }

  async getJpClassTypeStaffOverrides(id: number, options?: JpClassTypeOverrideFetchOptions): Promise<Array<JpClassTypeOverrideStaff>> {
    const { data } = await this.client.get<unknown>(`/jpclasstype/staff/${id}/overrides`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeOverrideFetchOptions") });
    return deserializeCbiDto<Array<JpClassTypeOverrideStaff>>(data, "generated.dto.JpClassTypeOverrideStaff");
  }

  async getJpClassTypeOverrideStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/jpclasstypeoverride/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getJpClassTypeOverrideStaffTypeId(id: number, options?: JpClassTypeFetchOptions): Promise<JpClassTypeStaff> {
    const { data } = await this.client.get<unknown>(`/jpclasstypeoverride/staff/${id}/typeid`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeFetchOptions") });
    return deserializeCbiDto<JpClassTypeStaff>(data, "generated.dto.JpClassTypeStaff");
  }

  async getJpClassStaffTypeId(id: number, options?: JpClassTypeFetchOptions): Promise<JpClassTypeStaff> {
    const { data } = await this.client.get<unknown>(`/jpclass/staff/${id}/typeid`, { params: serializeCbiDto(options, "generated.dto.JpClassTypeFetchOptions") });
    return deserializeCbiDto<JpClassTypeStaff>(data, "generated.dto.JpClassTypeStaff");
  }

  async getJpClassStaffLocationId(id: number, options?: ClassLocationFetchOptions): Promise<ClassLocationStaff | null> {
    const { data } = await this.client.get<unknown>(`/jpclass/staff/${id}/locationid`, { params: serializeCbiDto(options, "generated.dto.ClassLocationFetchOptions") });
    return deserializeCbiDto<ClassLocationStaff | null>(data, "generated.dto.ClassLocationStaff");
  }

  async getJpClassStaffInstructorId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/jpclass/staff/${id}/instructorid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async getJpClassStaffSessions(id: number, options?: JpClassSessionFetchOptions): Promise<Array<JpClassSessionStaff>> {
    const { data } = await this.client.get<unknown>(`/jpclass/staff/${id}/sessions`, { params: serializeCbiDto(options, "generated.dto.JpClassSessionFetchOptions") });
    return deserializeCbiDto<Array<JpClassSessionStaff>>(data, "generated.dto.JpClassSessionStaff");
  }

  async getJpClassStaffSignups(id: number, options?: JpClassSignupFetchOptions): Promise<Array<JpClassSignupStaff>> {
    const { data } = await this.client.get<unknown>(`/jpclass/staff/${id}/signups`, { params: serializeCbiDto(options, "generated.dto.JpClassSignupFetchOptions") });
    return deserializeCbiDto<Array<JpClassSignupStaff>>(data, "generated.dto.JpClassSignupStaff");
  }

  async getJpClassStaffStaggers(id: number, options?: JpClassStaggerFetchOptions): Promise<Array<JpClassStaggerStaff>> {
    const { data } = await this.client.get<unknown>(`/jpclass/staff/${id}/staggers`, { params: serializeCbiDto(options, "generated.dto.JpClassStaggerFetchOptions") });
    return deserializeCbiDto<Array<JpClassStaggerStaff>>(data, "generated.dto.JpClassStaggerStaff");
  }

  async getJpClassSessionStaffInstanceId(id: number, options?: JpClassFetchOptions): Promise<JpClassStaff> {
    const { data } = await this.client.get<unknown>(`/jpclasssession/staff/${id}/instanceid`, { params: serializeCbiDto(options, "generated.dto.JpClassFetchOptions") });
    return deserializeCbiDto<JpClassStaff>(data, "generated.dto.JpClassStaff");
  }

  async getJpClassSessionStaffAttendance(id: number, options?: JpClassAttendanceFetchOptions): Promise<Array<JpClassAttendanceStaff>> {
    const { data } = await this.client.get<unknown>(`/jpclasssession/staff/${id}/attendance`, { params: serializeCbiDto(options, "generated.dto.JpClassAttendanceFetchOptions") });
    return deserializeCbiDto<Array<JpClassAttendanceStaff>>(data, "generated.dto.JpClassAttendanceStaff");
  }

  async getJpClassStaggerStaffInstanceId(id: number, options?: JpClassFetchOptions): Promise<JpClassStaff> {
    const { data } = await this.client.get<unknown>(`/jpclassstagger/staff/${id}/instanceid`, { params: serializeCbiDto(options, "generated.dto.JpClassFetchOptions") });
    return deserializeCbiDto<JpClassStaff>(data, "generated.dto.JpClassStaff");
  }

  async getJpClassSignupStaffWlResult(id: number, options?: JpClassWlResultFetchOptions): Promise<JpClassWlResultStaff | null> {
    const { data } = await this.client.get<unknown>(`/jpclasssignup/staff/${id}/wlresult`, { params: serializeCbiDto(options, "generated.dto.JpClassWlResultFetchOptions") });
    return deserializeCbiDto<JpClassWlResultStaff | null>(data, "generated.dto.JpClassWlResultStaff");
  }

  async getJpClassSignupStaffInstanceId(id: number, options?: JpClassFetchOptions): Promise<JpClassStaff> {
    const { data } = await this.client.get<unknown>(`/jpclasssignup/staff/${id}/instanceid`, { params: serializeCbiDto(options, "generated.dto.JpClassFetchOptions") });
    return deserializeCbiDto<JpClassStaff>(data, "generated.dto.JpClassStaff");
  }

  async getJpClassSignupStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    const { data } = await this.client.get<unknown>(`/jpclasssignup/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff>(data, "generated.dto.PersonStaff");
  }

  async getJpClassSignupStaffAttendance(id: number, options?: JpClassAttendanceFetchOptions): Promise<Array<JpClassAttendanceStaff>> {
    const { data } = await this.client.get<unknown>(`/jpclasssignup/staff/${id}/attendance`, { params: serializeCbiDto(options, "generated.dto.JpClassAttendanceFetchOptions") });
    return deserializeCbiDto<Array<JpClassAttendanceStaff>>(data, "generated.dto.JpClassAttendanceStaff");
  }

  async getJpClassAttendanceStaffSessionId(id: number, options?: JpClassSessionFetchOptions): Promise<JpClassSessionStaff> {
    const { data } = await this.client.get<unknown>(`/jpclassattendance/staff/${id}/sessionid`, { params: serializeCbiDto(options, "generated.dto.JpClassSessionFetchOptions") });
    return deserializeCbiDto<JpClassSessionStaff>(data, "generated.dto.JpClassSessionStaff");
  }

  async getJpClassAttendanceStaffSignupId(id: number, options?: JpClassSignupFetchOptions): Promise<JpClassSignupStaff> {
    const { data } = await this.client.get<unknown>(`/jpclassattendance/staff/${id}/signupid`, { params: serializeCbiDto(options, "generated.dto.JpClassSignupFetchOptions") });
    return deserializeCbiDto<JpClassSignupStaff>(data, "generated.dto.JpClassSignupStaff");
  }

  async getJpClassWlResultStaffSignupId(id: number, options?: JpClassSignupFetchOptions): Promise<JpClassSignupStaff> {
    const { data } = await this.client.get<unknown>(`/jpclasswlresult/staff/${id}/signupid`, { params: serializeCbiDto(options, "generated.dto.JpClassSignupFetchOptions") });
    return deserializeCbiDto<JpClassSignupStaff>(data, "generated.dto.JpClassSignupStaff");
  }

  async getClassInstanceStaff(ref: JpClassRef): Promise<JpClassStaff> {
    const { data } = await this.client.get<unknown>(`/jpclass/staff/getclassinstance`, { params: { ref: encodeRequestParam(ref, "generated.dto.JpClassRef") } });
    let result = data as JpClassStaff;
    result = deserializeCbiDto<JpClassStaff>(result, "generated.dto.JpClassStaff");
    return result;
  }

  async getClassInstanceMember(ref: JpClassRef): Promise<JpClassMember> {
    const { data } = await this.client.get<unknown>(`/jpclass/member/getclassinstance`, { params: { ref: encodeRequestParam(ref, "generated.dto.JpClassRef") } });
    let result = data as JpClassMember;
    result = deserializeCbiDto<JpClassMember>(result, "generated.dto.JpClassMember");
    return result;
  }

  async summaryLabelStaff(target: JpClassRef): Promise<string> {
    const { data } = await this.client.post<unknown>(`/jpclass/staff/summarylabel`, serializeCbiDto(target, "generated.dto.JpClassRef"));
    return data as string;
  }

  async summaryLabelMember(target: JpClassRef): Promise<string> {
    const { data } = await this.client.post<unknown>(`/jpclass/member/summarylabel`, serializeCbiDto(target, "generated.dto.JpClassRef"));
    return data as string;
  }
}

export const useJpClassApi = (): JpClassApi => new JpClassApiClient();
